/**
 * Sidebar video directory: switch panels from left nav, lazy-load YouTube iframes.
 * Mobile: bordered dropdown with +/- category rows.
 */
if (!customElements.get('video-tabs-sidebar')) {
  class VideoTabsSidebar extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.categoryToggles = Array.from(this.querySelectorAll('[data-category-toggle]'));
      this.mobileSelect = this.querySelector('[data-mobile-select]');
      this.mobileTrigger = this.querySelector('[data-mobile-trigger]');
      this.mobileTriggerLabel = this.querySelector('[data-mobile-trigger-label]');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab));
        tab.addEventListener('keydown', (e) => this.onTabKeydown(e, tab));
      });

      this.categoryToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.toggleCategory(toggle));
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleCategory(toggle);
          }
        });
      });

      if (this.mobileTrigger && this.mobileSelect) {
        this.mobileTrigger.addEventListener('click', () => this.toggleMobileSelect());
        this.onDocumentClick = (e) => {
          if (!this.mobileSelect.classList.contains('is-open')) return;
          if (!this.mobileSelect.contains(e.target)) this.closeMobileSelect();
        };
        document.addEventListener('click', this.onDocumentClick);
      }

      const initial =
        this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (initial) {
        const panel = this.panelForTab(initial);
        if (panel) this.activatePanelLazy(panel);
        this.syncMobileTriggerLabel(initial);
      }
    }

    disconnectedCallback() {
      if (this.onDocumentClick) {
        document.removeEventListener('click', this.onDocumentClick);
      }
    }

    panelForTab(tab) {
      const panelId = tab.getAttribute('aria-controls');
      if (!panelId) return null;
      return this.panels.find((panel) => panel.id === panelId) || null;
    }

    toggleMobileSelect() {
      if (!this.mobileSelect || !this.mobileTrigger) return;
      const open = !this.mobileSelect.classList.contains('is-open');
      this.mobileSelect.classList.toggle('is-open', open);
      this.mobileTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    closeMobileSelect() {
      if (!this.mobileSelect || !this.mobileTrigger) return;
      this.mobileSelect.classList.remove('is-open');
      this.mobileTrigger.setAttribute('aria-expanded', 'false');
    }

    syncMobileTriggerLabel(tab) {
      if (!this.mobileTriggerLabel || !tab) return;
      const text = (tab.textContent || '').trim();
      if (text) this.mobileTriggerLabel.textContent = text;
    }

    toggleCategory(toggle) {
      const group = toggle.closest('[data-category-group]');
      if (!group) return;

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const next = !expanded;
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
      group.classList.toggle('is-collapsed', !next);

      const list = group.querySelector('[data-category-list]');
      if (list) list.hidden = !next;
    }

    selectTab(tab) {
      if (!tab || !this.tabs.includes(tab)) return;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
        item.classList.toggle('is-active', selected);
      });

      this.panels.forEach((panel) => {
        const on = panel.id === tab.getAttribute('aria-controls');
        panel.toggleAttribute('hidden', !on);
        panel.classList.toggle('is-active', on);
        if (on) this.activatePanelLazy(panel);
      });

      this.syncMobileTriggerLabel(tab);
      this.closeMobileSelect();

      const group = tab.closest('[data-category-group]');
      if (group) {
        const toggle = group.querySelector('[data-category-toggle]');
        if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
          this.toggleCategory(toggle);
        }
      }
    }

    activatePanelLazy(panel) {
      panel.querySelectorAll('iframe[data-video-src]').forEach((frame) => {
        const src = frame.getAttribute('data-video-src');
        if (src && !frame.getAttribute('src')) {
          frame.setAttribute('src', src);
        }
      });
    }

    onTabKeydown(e, tab) {
      const index = this.tabs.indexOf(tab);
      if (index < 0) return;

      let nextIndex = -1;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        nextIndex = Math.min(this.tabs.length - 1, index + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        nextIndex = Math.max(0, index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = this.tabs.length - 1;
      }

      if (nextIndex >= 0) {
        this.selectTab(this.tabs[nextIndex]);
        this.tabs[nextIndex].focus();
      }
    }
  }

  customElements.define('video-tabs-sidebar', VideoTabsSidebar);
}
